import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Empty, Flex, Input, List, Skeleton, Typography } from 'antd';
import Title from 'antd/es/typography/Title';
import { debounce } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getContentBySearchCondition } from 'src/apis/helpdesk/topic.api';
import AvatarCustom from 'src/components/v2/Avatar';
import { ITopicDetail } from 'src/types/helpdesk/topic.type';
import { getFirstLetters, scrollToReply } from 'src/utils/utils';
import styles from './SearchMessage.module.scss';
import { TopicContext } from 'src/contexts/topic.context';
const { Text } = Typography;

type Props = {
  showSearch: boolean;
  setShowSearch: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function SearchMessage({ showSearch, setShowSearch }: Props) {
  const [searchCondition, setSearchCondition] = useState('');
  const [searchResults, setSearchResults] = useState<ITopicDetail[]>([]);
  const { id } = useParams();
  const { currentTopicMessagesGroup, setPage } = useContext(TopicContext);

  const searchResultsQuery = useQuery({
    queryKey: ['searchResults', id, searchCondition],
    queryFn: () => getContentBySearchCondition(String(id), searchCondition),
    enabled: !!searchCondition && !!id,
    staleTime: 60 * 1000
  });

  useEffect(() => {
    if (searchResultsQuery.data) {
      setSearchResults(searchResultsQuery.data.data);
    }
  }, [searchResultsQuery.data]);

  const debouncedSearch = useMemo(
    () =>
      debounce((term) => {
        if (!term) {
          setSearchResults([]);
          setSearchCondition(term);
        } else {
          setSearchCondition(term);
        }
      }, 500),
    []
  );

  const handleCancel = () => {
    setSearchCondition('');
    setSearchResults([]);
    setShowSearch(false);
  };

  const convertMessionInput = (text: string) => {
    if (!!text) {
      let regex = /@\[.+?\]\(.+?\)/gm;
      let displayRegex = /@\[.+?\]/g;
      let idRegex = /\(.+?\)/g;
      let matches = text?.match(regex);
      let arr: any[] = [];
      matches &&
        matches.forEach((m) => {
          let id = m.match(idRegex)?.[0].replace('(', '').replace(')', '');
          let display = m.match(displayRegex)?.[0].replace('@[', '').replace(']', '');

          arr.push({ id: id, display: display });
        });
      let newComment = text?.split(regex);
      let output = '';
      for (let i = 0; i < newComment.length; i++) {
        const c = renderContent(newComment[i]);

        if (i === newComment.length - 1) output += c;
        else output += c + `<a href="#" style="font-size: inherit;">@${arr[i].display}</a>`;
      }

      return output;
    }
    return '';
  };

  const renderContent = (content?: string) => {
    if (!content) return '';

    const index = content.toLowerCase().indexOf(searchCondition.toLowerCase());
    if (index === -1) return content;

    const searchLength = searchCondition.length;
    return `${content.substring(0, index)}<span style="font-weight: 500; color:#1b1b1b ">${content.substring(
      index,
      index + searchLength
    )}</span>${content.substring(index + searchLength)}`;
  };

  const handleScrollToOrigin = (topicDetailId: string) => {
    const index = currentTopicMessagesGroup?.findIndex((x) => x?.id === topicDetailId);
    const pageOfIndex = Math.ceil((index || 0) / 10);

    setPage((prev) => {
      if (pageOfIndex > prev) {
        return pageOfIndex;
      }
      return prev;
    });

    scrollToReply(String(topicDetailId), 'center');
  };

  return (
    <div className={styles.search}>
      <Flex vertical gap={16}>
        <Flex gap={8}>
          <Input
            className={styles.search_input}
            prefix={<SearchOutlined className={styles.search_icon} />}
            placeholder='Tìm kiếm'
            allowClear
            onChange={(event) => debouncedSearch(event.target.value)}
          />
          <Button className={styles.search_cancel} type='text' onClick={handleCancel}>
            Đóng
          </Button>
        </Flex>
        <div className={styles.search_result}>
          <Title>Kết quả tìm kiếm ({searchResults.length || 0})</Title>
          <List
            size='small'
            locale={{
              emptyText: (
                <Empty
                  image='https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg'
                  imageStyle={{ height: 100 }}
                  description='Không có kết quả được tìm thấy'
                />
              )
            }}
            dataSource={searchResults}
            renderItem={(item) => (
              <List.Item
                className={styles.search_result_item}
                key={item.senderBy.name}
                onClick={() => handleScrollToOrigin(String(item.id))}
              >
                <Skeleton loading={searchResultsQuery.isLoading} paragraph={{ rows: 1 }} active avatar>
                  <List.Item.Meta
                    avatar={
                      <AvatarCustom
                        size='medium'
                        background={String(item?.gradientBackground?.backgroundImage)}
                        text={item?.id === '0' ? 'All' : getFirstLetters(String(item.senderBy?.name))}
                      />
                    }
                    title={<Text strong>{item.senderBy?.name}</Text>}
                    description={
                      <p
                        style={{ fontSize: 12 }}
                        dangerouslySetInnerHTML={{
                          __html: String(convertMessionInput(String(item?.content))?.replace(/\n\r?/g, '<br />'))
                        }}
                      />
                    }
                  />
                </Skeleton>
              </List.Item>
            )}
          />
        </div>
      </Flex>
    </div>
  );
}
